/**
 * waitlistService.js — Send waitlist signups to Google Sheets
 *
 * Uses a Google Apps Script web app as a free webhook endpoint.
 * The script receives POST data and appends rows to a Google Sheet.
 *
 * Setup instructions:
 * 1. Create a Google Sheet
 * 2. Go to Extensions → Apps Script
 * 3. Paste the doPost function (see below)
 * 4. Deploy as Web App (Anyone can access)
 * 5. Copy the URL and paste it as GOOGLE_SHEET_WEBHOOK_URL below
 *
 * Google Apps Script code to paste:
 * ─────────────────────────────────
 * function doPost(e) {
 *   var sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
 *   var data = JSON.parse(e.postData.contents);
 *   sheet.appendRow([
 *     new Date(),
 *     data.email,
 *     data.salaryBracket,
 *     data.moneyWorry,
 *     data.source || 'web'
 *   ]);
 *   return ContentService
 *     .createTextOutput(JSON.stringify({ status: 'ok' }))
 *     .setMimeType(ContentService.MimeType.JSON);
 * }
 * ─────────────────────────────────
 */

// ⚠️ REPLACE THIS with your deployed Google Apps Script Web App URL
const GOOGLE_SHEET_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbxA-GiTUjOaQYJsGE0UF3BJmwq-LrMAuUEzZpjF4vdfkXI9JtDUOzRlsKujbr3YoiF5/exec";

/**
 * Submit a waitlist entry.
 * @param {{ email: string, salaryBracket: string, moneyWorry: string }} data
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export async function submitWaitlistEntry(data) {
    // Validate email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!data.email || !emailRegex.test(data.email.trim())) {
        return { success: false, message: 'Please enter a valid email address.' };
    }

    if (!data.salaryBracket) {
        return { success: false, message: 'Please select your salary bracket.' };
    }

    if (!data.moneyWorry) {
        return { success: false, message: 'Please select your biggest money worry.' };
    }

    const payload = {
        email: data.email.trim().toLowerCase(),
        salaryBracket: data.salaryBracket,
        moneyWorry: data.moneyWorry,
        source: data.source || 'spenderly-mvp',
        timestamp: new Date().toISOString()
    };

    // Try sending to Google Sheets webhook
    if (GOOGLE_SHEET_WEBHOOK_URL) {
        try {
            await fetch(GOOGLE_SHEET_WEBHOOK_URL, {
                method: 'POST',
                mode: 'no-cors', // Google Apps Script requires no-cors
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            // no-cors mode returns opaque response, so we can't check status
            // but if it didn't throw, it likely worked
            return { success: true, message: 'You\'re on the list! We\'ll email you when saved profiles launch.' };
        } catch (err) {
            console.error('Waitlist webhook failed:', err);
            // Fall through to local-only save
        }
    }

    // If no webhook configured or it failed, still save locally
    // so data isn't lost
    try {
        const { db } = await import('./db');
        await db.waitlist.add({
            email: payload.email,
            salaryBracket: payload.salaryBracket,
            moneyWorry: payload.moneyWorry,
            timestamp: payload.timestamp
        });
    } catch (e) {
        console.error('Local waitlist save failed:', e);
    }

    if (!GOOGLE_SHEET_WEBHOOK_URL) {
        return { success: true, message: 'You\'re on the list! We\'ll email you when saved profiles launch.' };
    }

    return { success: true, message: 'You\'re on the list! We\'ll email you when saved profiles launch.' };
}

// Salary bracket options for the form
export const SALARY_BRACKETS = [
    { value: '', label: 'Select your salary range' },
    { value: 'below-15k', label: 'Below ₹15,000' },
    { value: '15k-25k', label: '₹15,000 – ₹25,000' },
    { value: '25k-40k', label: '₹25,000 – ₹40,000' },
    { value: '40k-60k', label: '₹40,000 – ₹60,000' },
    { value: '60k-1L', label: '₹60,000 – ₹1,00,000' },
    { value: '1L-2L', label: '₹1,00,000 – ₹2,00,000' },
    { value: 'above-2L', label: 'Above ₹2,00,000' },
    { value: 'prefer-not', label: 'Prefer not to say' }
];

// Money worry options for the form
export const MONEY_WORRIES = [
    { value: '', label: 'Select your biggest worry' },
    { value: 'broke-by-month-end', label: 'Always broke by month-end' },
    { value: 'dont-know-where-salary-goes', label: "Don't know where my salary goes" },
    { value: 'too-many-subscriptions', label: 'Too many subscriptions' },
    { value: 'cant-save-for-goals', label: "Can't save for goals" },
    { value: 'impulse-spending', label: 'Impulse spending on food & shopping' },
    { value: 'emis-eating-salary', label: 'EMIs eating my salary' },
    { value: 'no-emergency-fund', label: 'No emergency fund' },
    { value: 'other', label: 'Something else' }
];
